import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import styled from 'styled-components';
import HeatmapVisualization from './HeatmapVisualization';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
  font-size: 1rem;
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 2s linear infinite;
  margin-right: 12px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  padding: 16px;
  background-color: #fee2e2;
  border-radius: 8px;
  color: #b91c1c;
  margin-bottom: 16px;
`;

const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
  color: #6b7280;
  text-align: center;
`;

/**
 * Container component that fetches correlation data and renders the heatmap
 * 
 * @param {Object} props
 * @param {Array} props.contentTypes - Content types to query
 * @param {Object} props.xAxis - Configuration for x-axis
 * @param {Object} props.yAxis - Configuration for y-axis
 * @param {Object} [props.findQuery] - Additional find query criteria
 * @param {Array} [props.activeFilters] - Active filters to apply
 * @param {Function} [props.onCellClick] - Callback for cell click
 * @param {Function} [props.onRowClick] - Callback for row click
 */
const CorrelationHeatmapContainer = ({
  contentTypes,
  xAxis,
  yAxis,
  findQuery = { kp_published_status: "published" },
  activeFilters = [],
  onCellClick,
  onRowClick
}) => {
  // Validate required fields
  const isConfigValid = 
    Array.isArray(contentTypes) && 
    contentTypes.length > 0 &&
    xAxis && 
    xAxis.valuePath &&
    xAxis.displayPath &&
    yAxis && 
    yAxis.valuePath &&
    yAxis.displayPath;
console.log({isConfigValid})
  // Construct API configuration
  const apiConfig = {
    contentTypes,
    xAxis,
    yAxis,
    findQuery,
    activeFilters
  };

  // Fetch correlation data
  const { 
    data, 
    isLoading, 
    isError, 
    error,
    refetch
  } = useQuery(
    ['correlationCount', apiConfig],
    async () => {
      if (!isConfigValid) {
        throw new Error('Invalid configuration: Missing required fields');
      }

      // Encode the config object for the URL
      const encodedConfig = encodeURIComponent(JSON.stringify(apiConfig));
      
      // Make the API call
      const { data } = await axios.get(
        `/api/insightMatrix/getCorrelationCount?configs=${encodedConfig}`
      );
      
      return data;
    },
    {
      // Don't run the query if config is invalid
      enabled: !!isConfigValid,
    }
  );

  // Handle loading state
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        Loading correlation data...
      </LoadingContainer>
    );
  }

  // Handle error state
  if (isError) {
    return (
      <div>
        <ErrorContainer>
          <h3>Error loading data</h3>
          <p>{error?.message || "Unknown error occurred"}</p>
        </ErrorContainer>
        <button 
          onClick={() => refetch()}
          style={{
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Handle no data
  if (!data || !data.xValues?.length || !data.yValues?.length) {
    return (
      <NoDataContainer>
        <p>No correlation data available for the selected criteria.</p>
        <p style={{ fontSize: '0.875rem', marginTop: '8px' }}>
          Try adjusting your filters or selecting different fields.
        </p>
      </NoDataContainer>
    );
  }

  // Render the visualization component with the fetched data
  return (
    <HeatmapVisualization 
      data={data}
      onCellClick={onCellClick}
      onRowClick={onRowClick}
    />
  );
};

export default CorrelationHeatmapContainer;